// A venue screen's picture held still on a signal from the room, and given
// back to the live tour on the next (cycle venue-screen-freeze, for a shoot
// in a dark lounge): the guide walks on and the internet notices nothing.
//
// The signal is the venue host's own: the guide process that serves these
// pages keeps the freeze and says it at `/control/events` on the same origin
// (text/event-stream; each event is the whole freeze, not a change):
//
//   { "on": boolean, "screens": "all" | ( "wall" | "49" | "55" )[], "at": ISO }
//
// Only a venue shell listens (venue-shell.js, roles venue-*), and only on its
// own origin, so nothing on the internet can reach a screen through it, and
// a page anywhere else (the lab, a local build, Netlify) finds no endpoint
// and listens to nothing.

// The shell's `body[data-screen]` and the name the room's remote gives the
// same screen.
const SCREEN_NAMES = Object.freeze( { wall: 'wall', extra: '49', 'tower-map': '55' } );

export const CONTROL_EVENTS_PATH = '/control/events';

/** Whether a page in `role` (lib/roles.js currentRole) listens for the
 *  freeze: a venue screen's, never a visitor's. */
export function listensForFreeze( role ) {
  return typeof role === 'string' && role.startsWith( 'venue-' );
}

/** The remote's name for the screen a shell is (`body[data-screen]`), or
 *  null for one the remote does not name. */
export function screenNameOf( screen ) {
  return Object.prototype.hasOwnProperty.call( SCREEN_NAMES, screen ) ? SCREEN_NAMES[ screen ] : null;
}

/** Whether the freeze `state` (an event's data, as text or parsed) holds the
 *  screen called `name`. Anything unreadable holds nothing: a screen frozen
 *  by a garbled message would stay frozen with nobody knowing why. */
export function frozenFor( state, name ) {
  let s = state;
  if ( typeof s === 'string' ) {
    try { s = JSON.parse( s ); } catch ( _err ) { return false; }
  }
  if ( !s || s.on !== true || !name ) return false;
  if ( s.screens === 'all' ) return true;
  return Array.isArray( s.screens ) && s.screens.includes( name );
}

/** Listen for the freeze on this page's own origin and call `onChange( held )`
 *  whenever whether `name` is held changes. Silent when the origin serves no
 *  freeze: a 404 closes the stream for good, and nothing is said. Returns a
 *  function that stops listening. */
export function listenForFreeze( name, onChange, { origin = window.location.origin } = {} ) {
  if ( !name || typeof EventSource !== 'function' ) return () => {};
  let held = false;
  const source = new EventSource( new URL( CONTROL_EVENTS_PATH, origin ).href );
  source.onmessage = ( e ) => {
    const now = frozenFor( e.data, name );
    if ( now === held ) return;
    held = now;
    onChange( held );
  };
  // A lost stream is not a freeze. While EventSource reconnects (the guide
  // restarted, which forgets the freeze) the screen goes on as it is, and the
  // state sent on reconnecting says what it should be.
  source.onerror = () => {};
  return () => source.close();
}

// A frozen frame's CSS animations stand where they are: the Tower's cursor
// breathes by a keyframe (hypericc-tower.css #cursor), which no hold on the
// scene reaches. Transitions are left alone: stopped half-way they would jump
// to their end, and one under way is over in a moment.
export const STILL_ANIMATIONS_CSS = '*, *::before, *::after { animation-play-state: paused !important; }';
const STILL_ANIMATIONS_ID = 'hyper-tour-still-animations';

/** Pause every CSS animation in `doc` until releaseAnimations( doc ). */
export function holdAnimations( doc ) {
  if ( !doc || !doc.documentElement || doc.getElementById( STILL_ANIMATIONS_ID ) ) return;
  const style = doc.createElement( 'style' );
  style.id = STILL_ANIMATIONS_ID;
  style.textContent = STILL_ANIMATIONS_CSS;
  ( doc.head || doc.documentElement ).appendChild( style );
}

export function releaseAnimations( doc ) {
  const style = doc && doc.getElementById( STILL_ANIMATIONS_ID );
  if ( style ) style.remove();
}

/** The page in `doc` as it stands, as a document that cannot move: its DOM
 *  without scripts or frames, with its own address as the base so its
 *  stylesheets resolve where they did. For a frame the shell covers while
 *  frozen (venue-shell.js coverWithStill); null for no document. */
export function stillCopyOf( doc ) {
  if ( !doc || !doc.documentElement ) return null;
  const root = doc.documentElement.cloneNode( true );
  for ( const el of root.querySelectorAll( 'script, iframe, base' ) ) el.remove();
  const head = root.querySelector( 'head' );
  if ( head ) {
    const base = doc.createElement( 'base' );
    base.setAttribute( 'href', doc.baseURI );
    head.prepend( base );
  }
  return `<!doctype html>${ root.outerHTML }`;
}

// The 360 (panopoint-viewer.js) turns on a renderer of its own, eased toward
// each look the page sends from the tourstate it hears: neither the hold on
// the scene's drawing nor its pause reaches it. A frozen page sends no look
// (hyper-tour-follow _lookInPano); this stops the turn already under way,
// where the view is now (`hyper-panopoint:hold`).
// Not by catching the looks on their way: they are dispatched on the window
// itself, where Chromium calls listeners in the order they were added, so a
// listener the shell adds later cannot come before the viewer's.

/** Stop the 360 in `win` where it is. */
export function stopTheLook( win ) {
  if ( win ) win.dispatchEvent( new win.CustomEvent( 'hyper-panopoint:hold' ) );
}
